import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { Input, Textarea } from '@material-tailwind/react'
import { NewContractContext } from './context'
import { CompanySelector, ContractSelector, MultiOfficeSelector } from '@/shared/selectors'
import { useGetCompanyQuery } from '@/store/services/company.service'
import CustomSelect from '@/shared/components/custom-select'
import { toOptions } from '@/utils'

export const GeneralInformationForm = () => {
  const { currentContract } =
    useContext(NewContractContext)
  const isEditMode = Boolean(currentContract?.id)
  const { t } = useTranslation()
  const { values, handleChange, setFieldValue } = useFormikContext()
  const previousCompanyIdRef = useRef(null)
  const { data: { company: selectedCompany } = {} } = useGetCompanyQuery(
    values.contractor_company_id,
    { skip: !values.contractor_company_id }
  )

  const currentCompany = useMemo(() => {
    if (!values.contractor_company_id) return {}
    if (selectedCompany?.id === values.contractor_company_id) return selectedCompany
    if (currentContract?.company?.id === values.contractor_company_id) return currentContract.company
    return {}
  }, [values.contractor_company_id, selectedCompany, currentContract?.company])
  const serviceAreaOptions = useMemo(
    () => toOptions({ data: currentCompany?.serviceAreas || [] }),
    [currentCompany?.serviceAreas]
  )
  const currentServiceAreas = useMemo(
    () => serviceAreaOptions.filter((option) => values.service_area_ids?.includes(option.value)),
    [serviceAreaOptions, values.service_area_ids]
  )
  const hasSingleServiceArea = serviceAreaOptions.length === 1
  const controlOptions = [
    { label: 'Contratista', value: 'contractor' },
    { label: 'Servicio esporádico', value: 'sporadic_service' }
  ]

  // handlers
  const handleCompanyChange = (option) => {
    setFieldValue('contractor_company_id', option?.value || null)
    setFieldValue('service_area_ids', [])
  }

  const handleOfficeChange = (options) => {
    setFieldValue('office_ids', options.map(option => option?.value))
  }

  useEffect(() => {
    if (!values.contractor_company_id) {
      previousCompanyIdRef.current = null
      if ((values.service_area_ids || []).length > 0) {
        setFieldValue('service_area_ids', [])
      }
      return
    }

    const availableIds = (currentCompany?.serviceAreas || []).map((serviceArea) => serviceArea.id)
    const selectedIds = values.service_area_ids || []
    const companyChanged = previousCompanyIdRef.current !== values.contractor_company_id

    if (companyChanged) {
      previousCompanyIdRef.current = values.contractor_company_id
      if (!isEditMode && selectedIds.length === 0) {
        if (availableIds.length === 1) {
          setFieldValue('service_area_ids', [availableIds[0]])
        } else if (availableIds.length > 1) {
          setFieldValue('service_area_ids', availableIds)
        }
      }
      return
    }

    if (availableIds.length === 1) {
      if (selectedIds.length !== 1 || selectedIds[0] !== availableIds[0]) {
        setFieldValue('service_area_ids', [availableIds[0]])
      }
      return
    }

    const filteredSelection = selectedIds.filter((id) => availableIds.includes(id))
    if (filteredSelection.length !== selectedIds.length) {
      setFieldValue('service_area_ids', filteredSelection)
    }
  }, [currentCompany?.serviceAreas, setFieldValue, values.service_area_ids, values.contractor_company_id, isEditMode])

  return (
    <>
      <div className='grid grid-cols-3 gap-5'>
        <Input
          required
          label={t('contract.one')}
          name="name"
          value={values.name}
          onChange={handleChange}
        />

        <CompanySelector
          value={values.contractor_company_id}
          onChange={handleCompanyChange}
          required
        />

        {
          currentContract?.id &&
          <ContractSelector
            reduceOptions={(_, curr) => curr.filter(opt => opt.value !== currentContract.id)}
            onChange={(opt = {}) => setFieldValue('ancestry', opt?.value ? String(opt?.value) : null)}
            value={Number(values.ancestry)}
            label="Contrato padre"
          />
        }
      </div>

      <div className='grid grid-cols-2 gap-5'>
        <CustomSelect
          isMulti
          required
          isDisabled={!values.contractor_company_id || hasSingleServiceArea}
          placeholder={t('serviceArea.one')}
          options={serviceAreaOptions}
          value={currentServiceAreas}
          onChange={(selectedOptions) => {
            setFieldValue(
              'service_area_ids',
              (selectedOptions || []).map((selectedOption) => selectedOption.value)
            )
          }}
        />

        <MultiOfficeSelector
          required
          value={values.office_ids}
          onChange={handleOfficeChange}
        />

        <Input
          required
          type="date"
          label={t('common.startDate.of', {
            what: t('contract.one').toLowerCase()
          })}
          name="start_date"
          value={values.start_date}
          onChange={handleChange}
        />
        <Input
          required
          type="date"
          label={t('common.finishDate.of', {
            what: t('contract.one').toLowerCase()
          })}
          name="finish_date"
          value={values.finish_date}
          onChange={handleChange}
        />

        <Input
          label='Código interno contrato'
          name="internal_contract_code"
          value={values.internal_contract_code || ''}
          onChange={(event) => {
            const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
            setFieldValue('internal_contract_code', value)
          }}
        />

        <CustomSelect
          required={false}
          placeholder='Tipo de control en APS'
          options={controlOptions}
          value={controlOptions.find(({ value }) => value === values.control_type)}
          onChange={(selectedOption) => setFieldValue('control_type', selectedOption?.value || '')}
        />

        <div className='col-span-2'>
          <Input
            label='Grupo'
            name='group'
            value={values.group || ''}
            onChange={(event) => setFieldValue('group', event.target.value.replace(/[\r\n]+/g, ' '))}
          />
        </div>

        <div className='col-span-2'>
          <Textarea
            label='Descripción de los servicios'
            name='services_description'
            rows={4}
            value={values.services_description || ''}
            onChange={handleChange}
          />
        </div>

      </div>
    </>
  )
}
